import {
    emptyProducts,
    skeletonProducts,
} from '@/features/pos/components/ProductCatalog/parts/ProductList/constants'
import { Item } from '@/features/pos/components/ProductCatalog/parts/ProductList/types'
import { ProductsQuery } from '@/features/pos/components/ProductCatalog/queries/products'
import { transformProduct } from '@/features/pos/components/ProductCatalog/utils/categories-with-products-transforms'
import { useDebouncedSearchProduct } from '@/features/pos/hooks/search-product/useDebouncedSearchProduct'
import { useHandleQueryErrorWithToast } from '@/features/pos/hooks/useHandleQueryErrorWithToast'
import { useLatestValueRef } from '@/hooks/useLatestValueRef'
import { useQuery } from '@apollo/client'
import Fuse from 'fuse.js'
import { useEffect, useMemo } from 'react'

export const useFoundProducts = (searchProduct: string): readonly Item[] => {
    const debouncedSearchProduct = useDebouncedSearchProduct(searchProduct)

    const { data, error, loading, refetch } = useQuery(ProductsQuery)

    useHandleQueryErrorWithToast(error, loading)

    useEffect(() => {
        if (debouncedSearchProduct) refetch()
    }, [debouncedSearchProduct, refetch])

    const fuse = useMemo(() => {
        if (!data) return undefined

        return new Fuse(data.products.map(transformProduct), { keys: ['name'] })
    }, [data])

    const loadingRef = useLatestValueRef(loading)

    return useMemo(() => {
        if (!fuse && loadingRef.current) return skeletonProducts

        if (!fuse) return emptyProducts

        return fuse.search(searchProduct).map(({ item }) => item)
    }, [fuse, loadingRef, searchProduct])
}
